import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import {
  getAnalysisJob,
  getAnalysisStatus,
  getAnalysis,
  getType,
} from "../../../network/cipAPI";
import AdvanceSettings from "../../analyseData/advancedSettings";
import {
  storeAnalysisData,
  storeTaskId,
} from "../../../Store/TemplateStore/template.action";

import ReplacementResultChart from "../charts";
import PageLoading from "../../../components/PageLoading";
import { OverlayLoader } from "../../../components/PageLoading";
import AgTable from "../../../components/AgTable";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../../components/Notifications";
import InputBox from "../../../components/InputBox";
import DupontButton from "../../../components/DupontButton";
import exportAllReplacementTables from "../../../XLSDownload/ExportAllTables/raTableExport";
import { TextFieldValidator } from "../../../components/InputBox/TextFieldValidator";

import ExportAllData from "./exportAllData";

import { RELACEMENT_ANALYSIS_COL_DEF } from "./replacementAnalysisTable.config";

import "./index.scss";

const ReplacementResult = ({
  setDisableNextButton,
  previousPredictionResult,
  previousForecastWindow,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const intervalIdRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const [processedPercentage, setprocessedPercentage] = useState("1%");
  const [taskId, setTaskId] = useState(useSelector((state) => state.taskId));
  const [analysisResult, setAnalysisResult] = useState(null);
  const [rowData, setRowData] = useState(null);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [translatedRowData, setTranslatedRowData] = useState(null);
  const [plotData, setPlotData] = useState(null);
  const [errorMsgFailedPrediction, setErrorMsgFailedPrediction] =
    useState(null);
  const plantLocation = useSelector((state) => state.plantLocation);
  const plantName = useSelector((state) => state.plantName);
  const selectedTrain = useSelector((state) => state.selTrain);
  const [exportAllSheetName, setExportAllSheetName] = useState(
    `${plantLocation.trim()}_${plantName.trim()}_${moment().format(
      "MM/DD/YYYY"
    )}_${t("replacementAnalysisStrings.replacementAdvisorReport")}`
  );
  const allData = ExportAllData();
  const [exportAllData, setExportAllData] = useState({
    ...allData,
    replacementAnalysisData: rowData,
    replacementPredictionData: plotData,
  });
  const [forecastDays, setForecastDays] = useState(
    useSelector((state) => state.forecast_window)
  );
  const [errorMsg, setErrorMsg] = useState(null);
  const colDef = RELACEMENT_ANALYSIS_COL_DEF(t, translatedRowData);

  const handleSettingsDialog = (open) => {
    setIsSettingsDialogOpen(open);
  };

  const handleAdvanceSettingsSave = () => {
    // Trigger the analysis API call again when advance settings are saved
    onReplacementResultClicked();
  };
  useEffect(() => {
    setExportAllData({
      ...exportAllData,
      replacementAnalysisData: rowData,
      replacementPredictionData: plotData,
    });
  }, [rowData, plotData]);

  const onForecastDaysChange = (value, name) => {
    value = parseInt(value) || null;
    setErrorMsg("");

    if (value && value >= 1 && value <= 15) {
      setForecastDays(value);
      setExportAllData((prevState) => ({
        ...prevState,
        others: [
          {
            ...prevState.others[0],
            forecast_window: value,
          },
        ],
      }));
    } else {
      setForecastDays(value);
      setErrorMsg(
        `${name.split("(")[0]} ${t(
          "defaultInformationStrings.shouldBeBetween"
        )} 1 ${t("defaultInformationStrings.and")} 15`
      );
    }
  };
  const checkStatus = async (task_id) => {
    try {
      let id = task_id || taskId;
      let analysisStatus;
      if (id === "" || id === null || id === undefined) {
        analysisStatus = { data: { status: "pending" } };
      } else {
        analysisStatus = await getAnalysisStatus(id);
      }

      const status = analysisStatus?.data?.status?.toLowerCase();
      try {
        if (status === "completed") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          const analysisResult = await getAnalysis(selectedTrain, forecastDays);
          setShowOverlayLoader(false);
          setIsLoading(false);
          setDisableNextButton(false);
          if (analysisResult?.data["modeling"]) {
            try {
              const parsedData = JSON.parse(analysisResult?.data["modeling"]);
              dispatch(storeAnalysisData(parsedData));
              setAnalysisResult(parsedData);
              setRowData(parsedData.replacement_results);
              setPlotData(parsedData.plot_results);
              dispatch(storeTaskId(""));
              setTaskId("");
            } catch (error) {
              createNotification(
                ERROR_NOTIFICATION,
                t("notificationStrings.somethingWrong"),
                ""
              );
            } finally {
              setDisableNextButton(false);
              setIsLoading(false);
            }
          }
          //  else {
          //   setIsLoading(false);
          // }
        } else if (status === "running") {
          setprocessedPercentage(analysisStatus?.data.processed);
          setIsLoading(true);
          setDisableNextButton(true);
          setShowOverlayLoader(false);
        } else if (status === "failed") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setIsLoading(false);
          setDisableNextButton(false);
          setShowOverlayLoader(false);
          createNotification(
            ERROR_NOTIFICATION,
            t("predictionStrings.failedPrediction"),
            ""
          );
          setErrorMsgFailedPrediction(t("predictionStrings.errorPrediction"));
          dispatch(storeTaskId(""));
          setTaskId("");
          setRowData(null);
          setPlotData(null);
          dispatch(storeAnalysisData(null));
        } else if (status === "pending") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setIsLoading(false);
          setShowOverlayLoader(false);
          setDisableNextButton(false);
        }
      } catch (error) {
        setShowOverlayLoader(false);
        setIsLoading(false);
        setDisableNextButton(false);
        console.error("Error fetching data:", error);
      }
    } catch (error) {
      if (error.code === "ERR_NETWORK" || error.response.status === 502) {
        console.log("Error is " + error);
      } else {
        console.error("Error fetching data:", error);
        setErrorMsgFailedPrediction(t("predictionStrings.errorPrediction"));
        setIsLoading(false);
        setDisableNextButton(false);
        setShowOverlayLoader(false);
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        dispatch(storeTaskId(""));
        setTaskId("");
      }
    }
  };

  const onReplacementResultClicked = async () => {
    setIsLoading(true);
    setDisableNextButton(true);
    setprocessedPercentage("1%");
    let task_id = taskId;

    if (!taskId) {
      setErrorMsgFailedPrediction(null);
      try {
        const convertedType = await getType();
        setTranslatedRowData(convertedType.data);
        let response = await getAnalysisJob(forecastDays, selectedTrain);
        task_id = response?.data?.task_id;
        dispatch(storeTaskId(task_id));
        setTaskId(task_id);

        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }
        intervalIdRef.current = setInterval(function () {
          checkStatus(task_id);
        }, 15000);
      } catch (error) {
        setIsLoading(false);
        setDisableNextButton(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          ""
        );
      }
    }
  };

  useEffect(() => {
    // Initial setup
    if (previousForecastWindow && previousPredictionResult) {
      // If we have previous data, use it directly
      const parsedData = JSON.parse(previousPredictionResult);
      dispatch(storeAnalysisData(parsedData));
      setAnalysisResult(parsedData);
      setRowData(parsedData.replacement_results);
      setPlotData(parsedData.plot_results);
      setForecastDays(previousForecastWindow);
      setDisableNextButton(false);
    } else {
      // If no previous data but there's a task ID, check its status
      const IsJobAlreadyRunning = async () => {
        await checkStatus(taskId);
      };
      IsJobAlreadyRunning();
    }

    // Cleanup function when component unmounts
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  return (
    <div className="ag-theme-alpine container-padding-ra">
      {showOverlayLoader && <OverlayLoader customHeight={"350px"} />}
      <div className="flex-center-center">
        <InputBox
          value={forecastDays}
          labelText={t("replacementAnalysisStrings.forecastWindow")}
          onChange={onForecastDaysChange}
          isRequired
          name={t("replacementAnalysisStrings.forecastWindow")}
          errorMsg={errorMsg}
          customClass="width30 dupont-form-label"
          validator={TextFieldValidator.NUMBER_ONLY}
          disabled={isLoading}
          hideClearBtn={isLoading}
        />
        <div className={`${errorMsg ? "margin-top-13" : "margin-top-20"}`}>
          <DupontButton
            onClick={onReplacementResultClicked}
            customClass="margin-left-10 "
            disabled={!forecastDays || isLoading || errorMsg}
          >
            {t("replacementAnalysisStrings.runReplacementAnalysis")}
          </DupontButton>
        </div>
        <div
          className={`text-right margin-left ${
            errorMsg ? "margin-top-13" : "margin-top-20"
          }`}
        >
          <DupontButton
            onClick={() =>
              exportAllReplacementTables(
                exportAllSheetName,
                exportAllData,
                translatedRowData,
                t
              )
            }
            customClass="margin-left-10"
            disabled={rowData === null || isLoading}
          >
            {t("predictionStrings.exportAll")}
          </DupontButton>
        </div>
      </div>
      {isLoading ? (
        <PageLoading
          customHeight={150}
          iconHeight={"height--100"}
          message={t("ValueStrings.loaderMessage")}
          percentage={processedPercentage}
        ></PageLoading>
      ) : (
        <div>
          {errorMsgFailedPrediction && (
            <div className="errorMsg">{errorMsgFailedPrediction}</div>
          )}

          {rowData && (
            <AgTable
              rowData={rowData}
              columnDefs={colDef}
              domLayout="autoHeight"
              name={`${t("excelSheetNames.replacementAnalysis")}.csv`}
            />
          )}
          {plotData && (
            <div className="margin-top-20 ">
              <span className="descriptionText">
                {t("replacementAnalysisStrings.chartDescription")}
              </span>
              <ReplacementResultChart plotData={plotData} />
            </div>
          )}

          {rowData && <div className="RO-disclaimer">{t("disclaimer")}</div>}
        </div>
      )}
      <DupontButton
        customClass="fixed-settings-btn"
        onClick={() => handleSettingsDialog(true)}
        disabled={isLoading}
      >
        {t("analyseData.settings")}
      </DupontButton>
      {isSettingsDialogOpen && (
        <AdvanceSettings
          isSettingsDialogOpen={isSettingsDialogOpen}
          handleSettingsDialogClose={() => handleSettingsDialog(false)}
          selectedTrain={selectedTrain}
          cipPredictionAndRecommendation
          lifetime
          onSave={handleAdvanceSettingsSave}
        />
      )}
    </div>
  );
};

export default ReplacementResult;
